import { useEffect } from 'react'

import { isChunkLoadError } from '@/lib/chunkLoadError'
import { recoverFromChunkError } from '@/lib/chunkRecovery'

/**
 * Stale-build recovery: after a deploy, sessions still on the previous buildId
 * request hashed chunks that no longer exist (404 → ChunkLoadError). Recover
 * with a same-origin hard navigation (see chunkRecovery).
 *
 * Scope: window error / unhandledrejection only — i.e. next/dynamic lazy
 * imports and other non-routing script loads. Route-transition asset failures
 * are already recovered by the Next.js router itself, which hard-navigates
 * right after emitting routeChangeError (router.js handleHardNavigation).
 *
 * Errors that bubble through React render (a next/dynamic chunk failing to
 * load) never reach these window handlers — those are handled by the
 * GlobalError ErrorBoundary, and any chunk error that still reaches Sentry is
 * dropped centrally by sentry.client.config.ts beforeSend.
 */
export const useStaleChunkReload = () => {
  useEffect(() => {
    const handleWindowError = (event: ErrorEvent) => {
      const error = event.error ?? event.message
      if (!isChunkLoadError(error)) return
      recoverFromChunkError(error)
    }

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (!isChunkLoadError(event.reason)) return
      recoverFromChunkError(event.reason)
    }

    window.addEventListener('error', handleWindowError)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)

    return () => {
      window.removeEventListener('error', handleWindowError)
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    }
  }, [])
}
